/**
 * Square render-fence + token geometry for the COMBAT.app map. The {@link CombatStage}
 * is a **2d-square-only** surface; the domain `Cell` is the tagged hex|square union.
 * This module is the
 * read/write boundary that narrows a combatant's anchor `Cell` to its square arm —
 * the square analogue of `frontend/src/lib/hexMap/fence.ts` (which fences the hex
 * surfaces). A hex cell reaching here is a bug (a hex encounter is the wrong
 * renderer), so {@link asSquareCell} throws loudly rather than silently mis-render.
 *
 * The footprint rect math **mirrors the domain** (`@lancer-vtt/space`'s square
 * `footprint`, `packages/space/src/square.ts`) so what the stage draws is exactly
 * what `move_token` validates: a Size-N unit's anchor is the centre cell (odd N) or
 * the min-corner (even N), and its footprint is the `ceil(N)²` block around/from it.
 */
import type { Cell, TerrainProperties, Combatant, DamageType, EdgeTerrain, EncounterState, GridExtent, SquareCell } from "@lancer-vtt/domain";
import { DEFAULT_HAZARD_TYPE, grappleDominantOf, grapplePartner, isCharacter, sideOf, squareCell, worldFor } from "@lancer-vtt/domain";
import { type Square, parseEdgeKey, parseSquareKey, squareKey } from "@lancer-vtt/space";
import type { WireCell } from "@lancer-vtt/shared";
import type { ActionDraft } from "../../lib/encounter/combatInspector";
import type { CellVisual, EdgeVisual } from "./terrainPalette";

// The render classes are declared in `terrainPalette.ts`, but the folds below
// (`cellChannels`/`edgeTreatment`) are their only producers, so both are re-exported here for every
// consumer that reads a fold's result.
export type { CellVisual, EdgeVisual };

/** World-space size of one grid cell, in px at scale 1 (the board lattice pitch). */
export const CELL = 40;

/**
 * Narrow a combatant's anchor {@link Cell} to the **square** arm at the render
 * fence. The square analogue of `asHex`: tolerant of an untagged cell (geometry
 * cells carry `{x,y}` with no `grid` tag), but a `grid: "hex"` cell reaching this
 * square-only surface is a hard error (square map editing is the only thing here —
 * a hex encounter must never silently render on it).
 */
export function asSquareCell(cell: Cell): SquareCell {
  if ("grid" in cell && cell.grid === "hex") {
    throw new Error(`asSquareCell: a hex cell reached the square combat surface (${JSON.stringify(cell)})`);
  }
  return cell as SquareCell;
}

/**
 * The combatant footprint's bounding box in **cell** coordinates: the min-corner
 * cell `(minX, minY)` and the side length `n` (in cells). Derived from the real
 * domain {@link footprint} so it matches `move_token`'s validated footprint exactly
 * — a Size-3 odd unit's box is the 3×3 centred on the anchor; a Size-2 even unit's
 * box is the 2×2 whose min-corner is the anchor.
 */
export function footprintBox(cell: SquareCell, size: number): { minX: number; minY: number; n: number } {
  const n = Math.max(1, Math.ceil(size));
  // Odd N: the anchor is the centre cell, so the box reaches back radius (n-1)/2. Even N: the
  // anchor IS the min-corner, so no reach-back. (Exactly `square.ts`'s `footprint` corner rule.)
  const back = n % 2 === 1 ? (n - 1) / 2 : 0;
  return { minX: cell.x - back, minY: cell.y - back, n };
}

/**
 * Invert a dropped footprint **min-corner** (`minCol`, `minRow`, in cells) back to
 * the unit's *anchor* cell, given the anchor's offset within its own footprint box.
 * The footprint shape is translation-invariant, so a drag that lands the box's
 * min-corner at `(minCol, minRow)` puts the anchor at `(minCol + offX, minRow + offY)`.
 */
export function anchorFromMinCorner(
  minCorner: { col: number; row: number },
  off: { x: number; y: number },
): SquareCell {
  return squareCell(minCorner.col + off.x, minCorner.row + off.y);
}

// ── Terrain + edge render geometry ────────────────────────────────────────────────
//
// `EncounterState.terrain` is keyed by the square cell key (`"x,y"`) and `edges` by
// the canonical shared-edge key (`"x0,y0|x1,y1"`, sorted — `@lancer-vtt/space`'s
// `edgeKey`). These helpers invert those keys back to lattice points + world-space
// geometry so the stage can draw the stored layer. They lean on the space package's
// own canonical parsers (`parseSquareKey`/`parseEdgeKey`) rather than re-deriving the
// key format, so the renderer and the domain agree on what a key means.

/** Parse a square terrain cell key (`"x,y"`) back to its lattice point. */
export function parseTileKey(key: string): Square {
  return parseSquareKey(key);
}

/** The two square cells a canonical edge key (`"x0,y0|x1,y1"`) joins, in canonical order. */
export function parseEdgeCells(key: string): [Square, Square] {
  const [ka, kb] = parseEdgeKey(key);
  return [parseSquareKey(ka), parseSquareKey(kb)];
}

/**
 * The world-space line segment (px at scale 1) for the **orthogonal** edge shared by
 * adjacent square cells `a` and `b` — the boundary they have in common. Returns `null`
 * for a non-orthogonal pair (a diagonal, or non-adjacent): the authoring tool only ever
 * writes orthogonal walls, and a diagonal "edge" shares a corner, not a boundary, so
 * there is no segment to draw. The segment lies on the cells' shared grid line: a
 * horizontal pair (differing in `x`) gives the vertical boundary at their max-x column,
 * a vertical pair (differing in `y`) gives the horizontal boundary at their max-y row.
 */
export function edgeSegment(a: Square, b: Square): { x1: number; y1: number; x2: number; y2: number } | null {
  if (a.y === b.y && Math.abs(a.x - b.x) === 1) {
    const bx = Math.max(a.x, b.x) * CELL; // shared vertical grid line
    const y = a.y * CELL;
    return { x1: bx, y1: y, x2: bx, y2: y + CELL };
  }
  if (a.x === b.x && Math.abs(a.y - b.y) === 1) {
    const by = Math.max(a.y, b.y) * CELL; // shared horizontal grid line
    const x = a.x * CELL;
    return { x1: x, y1: by, x2: x + CELL, y2: by };
  }
  return null;
}

// ── Terrain visual channels (a shared, renderer-agnostic render decision) ─────────────────────────
//
// The FOLD from a cell's terrain flags to the render classes it draws. It lives here, in exactly one
// renderer-agnostic place, rather than inline in `pixiCombatBoard.ts` where it would drift on what
// "block"/"hard cover"/"difficult terrain" mean. What each class LOOKS like is `terrainPalette.ts`'s
// job — the fold and the palette are separate concerns.

/** The floor channel's classes: what KIND of ground this is. Genuinely mutually exclusive — a cell
 *  is one kind of floor — which is why this is the one channel that still cascades. */
export type FloorVisual = Extract<CellVisual, "block" | "pit" | "difficult">;

/** The cover channel's classes — the cover grade a cell grants, independent of its floor. */
export type CoverVisual = Extract<CellVisual, "hard" | "soft">;

/** The sight channel's one class — the "you cannot see or shoot through this" veil. */
export type SightVisual = Extract<CellVisual, "sight">;

/** The hazard channel — `dangerous` (Core p.62), carrying the damage type it deals. The board draws
 *  the same amber rim for all five types today (`terrain-legibility` D10 puts a type glyph here at
 *  Phase 3b); the type is carried now so that glyph is a palette change, not another fold change. */
export type HazardChannel = { type: DamageType };

/**
 * The render classes one cell draws, **one per mechanical question** (`terrain-legibility` D1).
 *
 * Four orthogonal facts about a square used to compete for one mutually-exclusive slot, so three of
 * them were silently discarded whenever they shared a cell. Each now has its own channel, and every
 * non-`null` channel draws.
 */
export type CellChannels = {
  /** "Can I stand there, and what does it cost" — `null` ⇒ ordinary open floor. */
  floor: FloorVisual | null;
  /** "Does it protect whoever shelters there" — `null` ⇒ grants no cover. */
  cover: CoverVisual | null;
  /** "Can I see and shoot through it" — `null` ⇒ sightlines pass. */
  sight: SightVisual | null;
  /** "Will it hurt me" — `null` ⇒ harmless ground. */
  hazard: HazardChannel | null;
};

/**
 * The holo treatments a cell's terrain flags draw as — **one channel per mechanical question**
 * (`terrain-legibility` D1), replacing the mutually-exclusive `cellTreatment` this project was filed
 * against.
 *
 * ## Why it is four channels and not one class
 *
 * `TerrainProperties` carries four independent mechanical facts (passability/cost, cover grade, sight
 * blocking, hazard) and the old fold picked exactly one of them to draw. That was measurably lossy,
 * not merely terse: on eight `DEFAULT_MAPGEN` boards (seeds `"a"`–`"h"`, 40×40) **65.6% of difficult
 * terrain was never drawn** and **58.8% of cover cells silently cost 2 to enter** — mapgen writes
 * `cover` and `moveCost` onto the same cell *by design* ("scree skirting the outcrops"), so the
 * collision is the common case rather than an edge case. `combatCells.test.ts` pins those numbers
 * against the real generator so the loss cannot come back unnoticed.
 *
 * ## The two invariants
 *
 * **The floor channel cascades; nothing else does.** A cell is one kind of floor, so `block` /
 * `pit` / `difficult` are genuinely exclusive and keep their original predicates exactly:
 * a **full solid** obstacle (blocks movement AND sight, OR blocks flight — a boulder/building, or a
 * full-height wall a flyer can't cross) outranks a bare **pit**, and a `blocksFlight` cell reads as
 * `block` regardless of `blocksSight` — the CLI's `terrain set --solid --blocks-flight` (no
 * `--blocks-sight`) authors a floor-to-ceiling barrier that must NOT read as a pit (a pit is the one
 * thing a flyer expects to cross; a flight-blocking barrier is the opposite). **`block` is
 * deliberately not `blocksMovement && blocksFlight`**: the browser's Solid brush writes
 * `{blocksMovement, blocksSight}` with no flight flag (`combatAuthoring.ts`'s `tileFlagsFor`), so
 * that reading would render every hand-painted solid as a hole in the deck. `difficult` is last
 * because a move cost is meaningless on a square ground movement cannot enter at all.
 *
 * **No channel suppresses another.** Cover and sight dropped out of the cascade entirely — that is
 * the whole fix — so a cover-and-difficult cell now draws its stipple *and* its hatch. It also buys
 * a discrimination the old fold could not express: a `{blocksMovement, blocksSight}` cell draws the
 * solid panel **and** the sight veil, while a `{blocksMovement, blocksFlight}` cell draws the panel
 * alone. Those two were pixel-identical before and mean different things.
 *
 * Draw order is bottom-to-top `floor → cover → sight → hazard` (`pixiCombatBoard.ts`'s
 * `buildTerrainCell`): the floor is the ground, the hazard rim is the warning that must never be
 * covered up.
 */
export function cellChannels(flags: TerrainProperties): CellChannels {
  const blocksMovement = flags.blocksMovement === true;
  const floor: FloorVisual | null =
    blocksMovement && (flags.blocksSight === true || flags.blocksFlight === true)
      ? "block"
      : blocksMovement
        ? "pit"
        : (flags.moveCost ?? 1) >= 2
          ? "difficult"
          : null;
  return {
    floor,
    cover: flags.cover === "hard" || flags.cover === "soft" ? flags.cover : null,
    sight: flags.blocksSight === true ? "sight" : null,
    hazard: flags.dangerous === true ? { type: flags.hazard ?? DEFAULT_HAZARD_TYPE } : null,
  };
}

/**
 * The single treatment an EDGE's terrain flags draw as, or `null` for an edge the board does not
 * draw at all. The edge carrier's counterpart to {@link cellChannels}, and hoisted out of
 * `pixiCombatBoard.ts`'s wall loop for the same reason that fold lives here: the legend has to answer
 * "is there a low wall on this board?" with **the renderer's own predicate**, not a second copy of it
 * that can disagree about what counts (`terrain-legibility` D7 — a legend that can drift from the
 * board it explains is worse than no legend).
 *
 * `full` (blocks sight) is the crisp bright wall. The other three are all the **low** wall (blocks
 * ground movement only — flyable, sight passes), split by the cover grade it grants: `low` none,
 * `low-soft` soft, `low-hard` hard. Those three drew **pixel-identically** until 2026-09-01
 * (`CAPABILITIES.md`'s own named gap), so an authored grade was invisible on the board; the grade now
 * rides the wall's stroke pattern (the terrain painter's dash grammar, `terrainArt/paint.ts`).
 *
 * **A full wall's `cover` is deliberately ignored.** A full wall blocks the sightline outright, so
 * cover — which only matters when sight passes — has nothing to modify; the browser never writes one
 * (`combatAuthoring.ts`'s `edgeFlagsFor` drops `coverGrade` at `"full"`), and a CLI-written one would
 * be a grade with no effect. Splitting `full` by it would put rows in the legend for a distinction the
 * engine does not make.
 *
 * An edge that carries **only** a cover grade (RAW p.66's shoot-over, step-over fence —
 * `EdgeTerrain.cover` is deliberately not coupled to `blocksMovement`) still draws nothing and reads
 * `null` here, so the legend honestly declines to claim a wall for it. That is an unrendered carrier
 * rather than a discrimination failure, and closing it would be a NEW mark on the board rather than a
 * grade made visible — deliberately left for its own pass.
 */
export function edgeTreatment(flags: EdgeTerrain): EdgeVisual | null {
  if (flags.blocksSight === true) return "full";
  if (flags.blocksMovement !== true) return null;
  if (flags.cover === "hard") return "low-hard";
  if (flags.cover === "soft") return "low-soft";
  return "low";
}

// ── Token hit-testing ─────────────────────────────────────────────────────────────────────────────
//
// The ratified interaction model: a token's clickable region is resolved by plain footprint-AABB math
// against a native DOM pointer event, NOT Pixi's own `hitArea`/`eventMode` interactivity (there is no
// default automatic per-shape hit-graph pick to lean on). This is that math, pure and
// renderer-agnostic so it's unit-testable without a canvas at all.

/**
 * The topmost PLACED combatant whose footprint contains `world` (world px at scale 1), or `null`.
 * Tokens are drawn back-to-front in `combatants` order, so the LAST match (iterated in reverse) is
 * the one a real click would have hit first — the top-most token under the pointer wins. Used for
 * click-to-select AND as the drag-start probe (`CombatStage.tsx`'s native-DOM mousedown handler).
 */
export function hitTestToken(combatants: Combatant[], world: { x: number; y: number }): Combatant | null {
  for (let i = combatants.length - 1; i >= 0; i--) {
    const c = combatants[i]!;
    if (c.pos == null) continue;
    const sq = asSquareCell(c.pos);
    const { minX, minY, n } = footprintBox(sq, c.base.size);
    const side = n * CELL;
    const x0 = minX * CELL;
    const y0 = minY * CELL;
    if (world.x >= x0 && world.x < x0 + side && world.y >= y0 && world.y < y0 + side) return c;
  }
  return null;
}

/**
 * The token a press at `world` selects, where several share the point: a rider (a Jockeying or
 * Riding pilot) on its carrier's cell, or any other overlap. With nothing there selected it is the
 * topmost ({@link hitTestToken}). With `cycle` it is the token below the selected one, wrapping,
 * so every unit on a shared cell is reachable by clicking again (`pilot-command-menu` 2.3). The
 * stage presses with `cycle: false`, so a drag of the selected token drags it, and steps with
 * `cycle: true` on a lone press's still click (`CombatStage`'s `cyclePressRef`).
 */
export function pickToken(combatants: Combatant[], world: { x: number; y: number }, opts: { selectedId: string | null; cycle: boolean }): Combatant | null {
  const hits: Combatant[] = [];
  for (let rest = combatants; ; ) {
    const hit = hitTestToken(rest, world);
    if (!hit) break;
    hits.push(hit);
    rest = rest.filter((c) => c !== hit);
  }
  const at = hits.findIndex((c) => c.id === opts.selectedId);
  if (at < 0) return hits[0] ?? null;
  return opts.cycle ? hits[(at + 1) % hits.length]! : hits[at]!;
}

// ── Move-animation budget ─────────────────────────────────────────────────────────────────────────
//
// The Director's move planner (`CombatApp.tsx`'s `planMove`) traces an
// ALREADY-ACCEPTED move — budget-gating there is meaningless (the server already validated
// legality), so the only job of the "budget" it feeds `movePathAnchors` is to be generous
// enough that the reach-flood never truncates the real path.

/**
 * The move-animation budget for tracing an accepted move. `declaredSpeed` is usually a real
 * number and is generous enough on its own (the move was legal within it) — but a masked
 * hostile (the RAW class-tier disclosure default) has its Speed stripped to `undefined` on a
 * player's client: EVERY un-scanned enemy. `undefined <= 0` is `false` in JS, so a naive pass-through slips past
 * `movePathAnchors`' `budget <= 0` guard and then can never satisfy the reach-flood's
 * `cost <= budget` gate — collapsing the whole path to `null` (⇒ the token blinks to its
 * destination instead of walking it).
 *
 * When the declared speed is unknown, fall back to `width + height` — a bound that covers
 * any on-board path regardless of grid shape or path metric (mirrors the `range +
 * ceil(size)` generous-search idiom `teleportTargets` uses for the same "don't
 * under-search" reason — `packages/domain/src/encounter/movement.ts`). This NEVER
 * reveals/infers the real stripped stat — it only refuses to under-animate a move the
 * server already accepted.
 */
export function animateMoveBudget(declaredSpeed: number | undefined, extent: GridExtent): number {
  return declaredSpeed ?? extent.width + extent.height;
}

/**
 * The route a grapple partner is DRAGGED along: the straight line from its old cell to its new one,
 * the same line the drag recomputer walks (`packages/rules/src/encounter/dragRecompute.ts`). `null`
 * unless `c` is the non-dominant party of a live grapple. The planner falls back to this when
 * `movePathAnchors` has no route, which is always for a dragged partner: it is Immobilized and not
 * the acting unit. Without it the partner blinks to its new cell instead of being pulled along
 * (`projects/grapple-move-feel.md` Defect 6).
 */
export function draggedRoute(state: EncounterState, c: Combatant, step: { from: Cell; to: Cell }): Cell[] | null {
  const partner = grapplePartner(state, c.id);
  if (partner === undefined || grappleDominantOf(state, c.id, partner) !== partner) return null;
  const line = worldFor(state).line(step.from, step.to);
  return line.length >= 2 ? line : null;
}

// ── Overlay-mode exclusivity ──────────────────────────────────────────────────────────────────────
//
// The board's cell-dot overlays are modal — exactly ONE dot vocabulary active at a time. Without
// this the move-range wash and the attack-range wash stack (move dots gated only on
// `viewMode === "play" && selectedId`, never on the armed `aim`). `overlayModeFor` is the single
// derived selector `CombatApp.tsx` computes from state it already tracks (no new state of its own);
// each of the four overlay memos there ANDs its existing gate on the result so exactly one
// vocabulary renders. It lives here, not inlined in the component, so it unit-tests without a React
// renderer and so any other consumer imports the same selector rather than re-deriving the
// precedence.

/** The board's single active dot vocabulary, or `"none"` when nothing should render. */
export type OverlayMode = "movement" | "attack" | "tech" | "intel" | "none";

/** Aim modes whose reach-wash is the weapon-red attack overlay (Skirmish/Barrage, the melee verbs). */
// `activate_cell` is in the set: a thrown deployable is picked at a CELL within the action's RAW
// reach ("within Range 5"), which is exactly the reach-wash + hovered-template vocabulary this
// overlay already speaks, so the throw gets the same red wash a Blast weapon gets.
//
// `activate_sites` (directed multi-pick placement) is in it too, even though its wash is a DIFFERENT
// derivation than this vocabulary's `weaponRangeForAim`/`attackAffectedKeys` machinery —
// `legalDirectedDeployCells`, adjacency-derived from live state and the picks so far, recomputed per
// pick. It reuses the SAME rendering seam (`CombatApp.tsx`'s `cellSightlines` memo →
// `pixiCombatBoard.ts`'s `setTargeting`) the reach wash already paints through, rather than a
// parallel wash container: `weaponRangeForAim` still returns `null` for this mode (see that
// function's own comment), and `cellSightlines` gets its OWN branch that skips
// `armedCellSightlines`'s LoS/cover machinery entirely (RAW's placement constraint has no LoS/cover
// component) and maps `legalDirectedDeployCells`'s cells straight to
// `{visible: true, cover: "none"}`.
//
// **Adding a mode to this Set flips `overlayMode !== "attack"` from `true` to `false` for it, which
// un-suppresses every OTHER `"attack"`-gated consumer too.** That is the "a new mode doesn't inherit
// an existing short-circuit" trap; check each consumer explicitly:
//   - `attackLosExcluded` (`CombatApp.tsx`) needs an EXPLICIT `aim.mode` exclusion — for
//     `activate_sites` and `activate_cell` the resolution path applies no LoS filter at all, so
//     painting a "sheltered, spared" veil would be a lie. It cannot rely on `overlayMode !==
//     "attack"` returning `null` first.
//   - `attackAffected` (`CombatApp.tsx`) needs nothing — it calls `weaponRangeForAim(attacker, aim)`
//     and returns `null` when that's falsy, which it is for a placement pick.
//   - `outOfRangeTargetIds` (`CombatApp.tsx`) needs nothing — its `"attack"`-gated branch is ALSO
//     gated on `aim.mode === "skirmish" || aim.mode === "barrage"` first, which `activate_sites`
//     never satisfies, so it falls through to `null` before the `overlayMode` check is reached.
//   - `attackArea`/`cellPickArmed`/`targetPicking`/the press-routing branch in `onCommitCell` each
//     carry an explicit `activate_sites` case.
// `dismount`/`eject` are in the set too — the mech↔pilot bridge's own landing-cell wash
// (`legalPilotBridgeCells`, `attackRange.ts`) rides this SAME cell-dot rendering seam
// `activate_cell` established (a bare/occupied-cell click commits), even though its legality
// derivation (the real `checkDismountLegality`/`checkEjectLegality` wall) is its own thing, not
// `weaponRangeForAim`'s. `ride_dismount` joins the same way, off
// `legalRideDismountCells`/`checkRideDismountLegality`.
const ATTACK_OVERLAY_AIM_MODES: ReadonlySet<ActionDraft["mode"]> = new Set([
  "skirmish",
  "barrage",
  "activate_cell",
  "activate_sites",
  "dismount",
  "eject",
  "ride_dismount",
  // The melee verbs (`token-command-menu` Phase 3): their threat-1 reach (`meleeReachForAim`)
  // paints the same red wash, armed or hovered, instead of falling through to the movement wash.
  // Consumer check: `attackAffected`/`attackLosExcluded` read `weaponRangeForAim`, `null` for these;
  // `outOfRangeTargetIds` takes their own branches first; `attackArea`/`cellPickArmed` stay false.
  "ram",
  "grapple",
  "improvised_attack",
  // A pilot's Mount, Jockey and Ride (`pilot-command-menu` 2.4): the adjacent cells, the same way.
  "mount",
  "jockey",
  "ride",
]);

/**
 * Aim modes whose armed AoE-hover LoS-excluded veil (`CombatApp.tsx`'s `attackLosExcluded`) must
 * stand down UNCONDITIONALLY, regardless of `overlayMode` — the resolution path for both applies
 * NO LoS filter at all, so painting a "sheltered, spared" marker over a token inside the wash would
 * misrepresent what the resolver actually hits. `activate_cell` (a point-blanket throw) and
 * `activate_sites` (directed multi-pick placement) both qualify. Exported — rather than left as an
 * inline `aim.mode === "..."` check in
 * `CombatApp.tsx` — so this exclusion is independently pinnable by a unit test (`overlayMode.test.ts`)
 * without rendering the component; `attackLosExcluded` itself stays a `CombatApp.tsx`-local callback
 * (it closes over `state`/`overlayMode`), so the exclusion SET is what's shared, not the callback.
 */
// `dismount`/`eject` qualify too: the pilot-bridge resolution path applies no LoS filter at all (RAW
// gates the landing cell on adjacency/fly-6 + a free space, nothing about sightlines), so painting a
// "sheltered, spared" veil here would be the identical lie. `ride_dismount` qualifies for the
// identical reason — RAW's adjacency + free-space gate has no sightline component either.
export const LOS_VEIL_EXCLUDED_AIM_MODES: ReadonlySet<ActionDraft["mode"]> = new Set([
  "activate_cell",
  "activate_sites",
  "dismount",
  "eject",
  "ride_dismount",
]);

/**
 * Aim modes whose reach-wash is the Sensors-scoped tech overlay (Search/Quick Tech/Full Tech, and a
 * system-granted TECH ATTACK, e.g. Markerlight). Kept
 * in lockstep with `combatInspector.ts`'s `SENSORS_SCOPED_AIM_MODES` (same Sensors-vs-weapon-range
 * distinction, different consumer: that set drives `sensorReachCells`' wash geometry, this one
 * drives `overlayModeFor`'s precedence). Missing `"tech_attack"` here would mean `overlayModeFor`
 * falls through past step 2 to step 3 (`"movement"`) with a tech attack armed — `CombatApp.tsx`'s
 * `sensorReach` memo gates on `overlayMode === "tech"`, so the wash would never paint even though
 * `sensorReachCells` itself computes the right cells; and the movement wash would incorrectly paint
 * instead, breaking the overlay-exclusivity precedence.
 */
const TECH_OVERLAY_AIM_MODES: ReadonlySet<ActionDraft["mode"]> = new Set(["search", "quick_tech", "full_tech", "tech_attack"]);

/**
 * The board's active overlay mode — a pure function of the board-wide armed `aim`, the current
 * `selectedId`, `viewMode`, and whether a pending DECLARED-PHASE DIRECTION prompt is active.
 * Precedence:
 *
 *   1. A pending direction prompt (Hunter's Lunge and its `toward:"chosen"` siblings) outranks
 *      everything — the SAME reasoning `CombatApp.tsx`'s own
 *      "a pending direction prompt supersedes any armed attack aim" effect already applies to
 *      `aim` itself (it nulls the aim the instant one arrives), just also reflected here so the
 *      wash agrees even in the one render tick before that effect fires, and so it's not at the
 *      mercy of whatever `selectedId`/`viewMode` happened to be at the moment the prompt landed
 *      → `"movement"`. The WASH SOURCE for this case is `CombatApp.tsx`'s `reachable` memo
 *      reading `PromptRequest.reachableCells` (server-computed, display-only — see that field's
 *      own doc), never the ordinary selection-based flood; a `toward:"chosen"` prompt (no
 *      precomputed set — no anchor exists yet to derive one from) paints nothing, leaving only
 *      the crosshair cursor.
 *   2. An armed Skirmish/Barrage aim, or a melee verb (Ram, Grapple, Improvised Attack) → `"attack"`.
 *   3. An armed Search/Quick Tech/Full Tech aim → `"tech"`.
 *   4. Else, in PLAY with no aim, a hovered unit with intel to show (`opts.hoverIntel`,
 *      `projects/hover-intel.md`) → `"intel"`: its move zone and weapon rings, painted in place of
 *      the selection's wash while the pointer rests on it. The caller already suppresses the hover
 *      under a hovered menu row, a drag, touch input and EDIT, so this step only has to lose to
 *      the steps above.
 *   5. Else a selection in PLAY → `"movement"` — no separate "is it placed" check needed here: an
 *      unplaced/speedless selection already renders no dots because `reachableCells` (the domain rule
 *      `CombatApp.tsx`'s `reachable` memo calls) itself returns `null` for one, so gating on the bare
 *      `selectedId` can never show a phantom wash.
 *   6. Else `"none"`.
 *
 * Every other armed aim (Activate/Stabilize/Release) has no cell-dot
 * vocabulary of its own — it falls through to whichever of 5/6 applies. The exception is an Activate
 * whose action has a frozen reach (`opts.activateReach`, the caller's `activateReachForAim` read):
 * `"attack"`, so a hovered or armed Sniper's Mark paints its range (range-hover).
 */
export function overlayModeFor(
  aim: ActionDraft | null,
  selectedId: string | null,
  // `viewMode` + `directionPromptActive` are bundled into one 3rd param — a bare 4th positional
  // param trips `CODE_STYLE.md`'s `max-params-3` oxlint gate (`bun run lint`, not typecheck,
  // catches it).
  opts: { viewMode: "edit" | "play"; directionPromptActive: boolean; activateReach?: boolean; hoverIntel?: boolean },
): OverlayMode {
  if (opts.directionPromptActive) return "movement";
  if (aim && ATTACK_OVERLAY_AIM_MODES.has(aim.mode)) return "attack";
  // A single-target granted action with a frozen reach (`activateReachForAim`) washes it red too.
  if (aim?.mode === "activate" && opts.activateReach === true) return "attack";
  if (aim && TECH_OVERLAY_AIM_MODES.has(aim.mode)) return "tech";
  if (opts.viewMode === "play" && aim === null && opts.hoverIntel === true) return "intel";
  if (opts.viewMode === "play" && selectedId !== null) return "movement";
  return "none";
}

/**
 * The board's `"x,y"` cell-key set (`parseTileKey`/`setMoveRange`'s vocabulary) for a pending
 * direction prompt's server-computed `PromptRequest.reachableCells` — the movement-overlay wash's
 * source for that case, converted from the wire's tagged `WireCell[]`. `asSquareCell` fences a stray hex cell the same way the rest of
 * this module does (a hex encounter is the wrong renderer for this board). `undefined` (absent —
 * a `toward:"chosen"` prompt, or a mover the engine found no ray for) → an EMPTY set, never
 * `null`: the caller doesn't branch on presence, an empty wash is exactly the "no dots, keep the
 * crosshair" outcome that shape wants.
 */
export function reachableCellKeys(cells: readonly WireCell[] | undefined): Set<string> {
  const out = new Set<string>();
  if (!cells) return out;
  for (const c of cells) out.add(squareKey(asSquareCell(c as Cell)));
  return out;
}

/**
 * The single-character glyph for a combatant — **its own name's first letter**. The board token no
 * longer draws it (tokens show their art, `pixiTokenSprite.ts`); the UNITS/TERRAIN/deploy lists do.
 *
 * Deliberately NOT one letter per `CombatantKind`: a fixed per-kind vocabulary makes a list of
 * four hostiles read `N N N N` and every player mech read `M`, carrying strictly less information
 * than the name and never telling the table WHICH unit it is looking at.
 *
 * **Collisions are accepted, deliberately.** "Assault Alpha" and "Assault Bravo" both glyph `A`;
 * the name label beneath the token is what disambiguates them. Widening to two characters fits
 * poorly on a Size-1 token and only moves the collision rather than removing it.
 *
 * Iterates by code point (`for…of`) so an astral-plane first character survives intact, and takes
 * the first LETTER OR DIGIT so a decorative prefix ("«Vanguard»", "— unnamed") doesn't glyph as
 * punctuation. A name with no alphanumeric at all falls back to `"?"`.
 */
export function nameGlyph(name: string): string {
  for (const ch of name) {
    if (!/[\p{L}\p{N}]/u.test(ch)) continue;
    // Some code points uppercase to MORE than one (`ß` → `SS`); keep the glyph one code point.
    return [...ch.toUpperCase()][0] ?? ch;
  }
  return "?";
}

/** One token's render treatment — its ring (stroke) and fill colours. */
export type TokenTreatment = { accent: string; fill: string };

/** The token palette. */
const FRIEND_ACCENT = "#5fe3ff";
const FRIEND_FILL = "rgba(95, 227, 255, 0.16)";
const FOE_ACCENT = "#ff6cae";
const FOE_FILL = "rgba(255, 108, 174, 0.16)";
const NEUTRAL_ACCENT = "#9aa6bd";
const NEUTRAL_FILL = "rgba(154, 166, 189, 0.16)";
const RUBBLE_ACCENT = "#5a5f6b";
const RUBBLE_FILL = "rgba(90, 95, 107, 0.14)";

/**
 * The scenery treatment — a neutral slate ring, deliberately side-BLIND (see {@link tokenLook}).
 * Exported because the pending-deploy preview draws the same ring with no `Combatant` in hand
 * yet: the mark has to read as "the section goes HERE", which only holds if it is literally the
 * treatment the placed section will wear.
 */
export const SCENERY_TOKEN_LOOK: TokenTreatment = { accent: NEUTRAL_ACCENT, fill: NEUTRAL_FILL };

/**
 * The ring colours for a combatant's token: **colour is its SIDE.**
 *
 * **Colour is `sideOf()`, never `kind`.** The terraformer rig is the case that forces it: it is
 * `kind: "npc"` (it spawns from an `npc_class`) but `allegiance: "pc"` once handed to a player slot,
 * so a kind-keyed colour puts it in the LANCERS bucket, taking its turn on the PC side, while
 * drawing hostile pink. Kind is a content-authoring fact; side is the thing a player reads the board
 * for. Any owned NPC hits this.
 *
 * **Objects and deployables stay neutral regardless of side, and that is not an oversight.**
 * `spawnObject` stamps `allegiance: "npc"` on every crate and wall section as a default nobody
 * chose, so colouring scenery by side would turn every prop on the board hostile-pink and say
 * something false about it. They are terrain-like props (`contributesToTerrain`), not a team's
 * units, so their ring stays neutral slate.
 * {@link isCharacter} is the canonical split (mech/npc/pilot/drone), so a **drone** inherits its
 * owner's colour rather than reading neutral — a player's drone is theirs.
 *
 * A destroyed object/deployable overrides to the duller rubble treatment. Render-layer only.
 */
export function tokenLook(c: Combatant, destroyed: boolean): TokenTreatment {
  if (destroyed && (c.kind === "object" || c.kind === "deployable")) {
    return { accent: RUBBLE_ACCENT, fill: RUBBLE_FILL };
  }
  if (!isCharacter(c)) return SCENERY_TOKEN_LOOK;
  return sideOf(c) === "pc"
    ? { accent: FRIEND_ACCENT, fill: FRIEND_FILL }
    : { accent: FOE_ACCENT, fill: FOE_FILL };
}
